import { isPlatformBrowser } from '@angular/common';
import { APP_INITIALIZER, inject, makeEnvironmentProviders, PLATFORM_ID } from '@angular/core';
import { CookieConsentService } from '@solidgroup-agency/ui-shared-components';

const COOKIE_NAME = 'cookie-settings';
const COOKIE_DOMAIN = '.awo-care.org';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function setCookie(name: string, value: string): void {
  document.cookie =
    `${name}=${encodeURIComponent(value)}; domain=${COOKIE_DOMAIN}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax; Secure`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function deleteCookie(name: string): void {
  document.cookie =
    `${name}=; domain=${COOKIE_DOMAIN}; path=/; max-age=0; SameSite=Lax; Secure`;
}

function syncCookieToLocalStorage(): void {
  const value = localStorage.getItem(COOKIE_NAME);
  if (value) {
    setCookie(COOKIE_NAME, value);
  }
}

/**
 * Syncs cookie consent between subdomains (awo-care.org / app.awo-care.org)
 * by mirroring localStorage ↔ shared cookie on .awo-care.org.
 */
function initCrossDomainCookieSync(): () => void {
  const platformId = inject(PLATFORM_ID);
  const consentService = inject(CookieConsentService);

  return () => {
    if (!isPlatformBrowser(platformId)) return;

    const cookieValue = getCookie(COOKIE_NAME);
    const localValue = localStorage.getItem(COOKIE_NAME);

    if (cookieValue && !localValue) {
      // Other subdomain already has consent → mirror to localStorage, skip banner
      localStorage.setItem(COOKIE_NAME, cookieValue);
      consentService.showBanner.set(false);
      consentService.storageChecked.set(true);
      const settings = JSON.parse(cookieValue);
      consentService.analyticsConsent.set(settings.analytics ?? false);
    } else if (localValue && !cookieValue) {
      // This subdomain has consent but cookie missing → write shared cookie
      setCookie(COOKIE_NAME, localValue);
    }

    // Patch public methods to also sync the shared cookie
    const origAcceptAll = consentService.acceptAllCookies.bind(consentService);
    consentService.acceptAllCookies = () => {
      origAcceptAll();
      syncCookieToLocalStorage();
    };

    const origAcceptEssential = consentService.acceptEssentialOnly.bind(consentService);
    consentService.acceptEssentialOnly = () => {
      origAcceptEssential();
      syncCookieToLocalStorage();
    };

    const origToggle = consentService.toggleAnalyticsConsent.bind(consentService);
    consentService.toggleAnalyticsConsent = () => {
      origToggle();
      syncCookieToLocalStorage();
    };

    const origRevoke = consentService.revokeCookies.bind(consentService);
    consentService.revokeCookies = () => {
      origRevoke();
      deleteCookie(COOKIE_NAME);
    };
  };
}

/**
 * Provides cross-domain cookie consent sync for awo-care.org subdomains.
 * Add to customer-ui and website app.config providers.
 */
export function provideCrossDomainCookieConsent() {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,
      useFactory: initCrossDomainCookieSync,
      multi: true,
    },
  ]);
}
